/* eslint-disable @typescript-eslint/no-explicit-any */
import { Color } from '@deck.gl/core'
import { ClusterTileLayer as DeckGlClusterTileLayer } from '@deck.gl/carto'
import { TilejsonResult } from '@carto/api-client'
import { getFormatter } from '@carto/kepler.gl/utils'
import { TOOLTIP_FORMATS } from '@carto/kepler.gl/constants'
import { getDatasetType, getSchemeFromTilesetDataset } from 'features/builder/state/builderSliceUtils'
import { KeplerTable, LayerTypes, TilingScheme } from 'features/builder/types'
import { MapType } from 'features/maps/types'
import { getDebounceTime, getTileSize } from 'features/common/utils/cartoDeckGL'
import { cartoSpatialTileLoadOptions } from 'features/common/utils/deckGlWorkers'
import { DEFAULT_UNIQUE_ID_PROPERTY, DEFAULT_AGGREGATION_LEVEL_CLUSTER } from 'features/builder/ui/builderConstants'
import { getSpatialIndexFromGeoColumn } from 'features/builder/state/builderSliceUtils'
import { getDefaultAggregationExpColumnAliasForLayerType } from 'features/builder/state/spatialIndexUtils'
import SpatialIndexLayer, { spatialIndexVisConfigs } from './SpatialIndexLayer'

export const TEXT_LABEL_INDEX = 0
const RADIUS_RANGE_MINIMUM = 8
const RADIUS_RANGE_MAXIMUM = 80
const TEXT_OUTLINE_OPACITY = 64

const calculateTextFontSize = (radius: number) => {
  if (radius >= 80) return 24
  if (radius >= 72) return 24
  if (radius >= 56) return 20
  if (radius >= 40) return 16
  if (radius >= 24) return 13
  if (radius >= 8) return 11
  return 11
}

const calculateRadius = (
  properties: { [column: string]: number },
  stats: { min: number; max: number },
  radiusRange: [number, number],
  column: string
) => {
  const { min, max } = stats[column]
  const value = properties[column]

  // When there's a single cluster on the screen, min and max are equivalent, so we should return the maximum radius
  if (min === max) return radiusRange[1]

  const normalizedValue = (value - min) / (max - min)
  return radiusRange[0] + normalizedValue * (radiusRange[1] - radiusRange[0])
}

const DEFAULT_VIS_CONFIG = {
  ...spatialIndexVisConfigs,
  radiusRange: {
    type: 'number',
    isRanged: true,
    range: [RADIUS_RANGE_MINIMUM, RADIUS_RANGE_MAXIMUM],
    defaultValue: [12, 64]
  },
  strokeOpacity: {
    type: 'number',
    defaultValue: 1
  },
  thickness: {
    type: 'number',
    defaultValue: 2,
    label: 'layerVisConfigs.strokeWidth',
    isRanged: false,
    range: [0, 100],
    step: 0.1,
    group: 'stroke',
    property: 'thickness'
  },
  opacity: {
    type: 'number',
    defaultValue: 1
  },
  stroked: {
    type: 'boolean',
    defaultValue: false
  },
  isTextVisible: {
    type: 'boolean',
    defaultValue: true
  },
  colorAggregation: {
    type: 'string',
    defaultValue: null
  },
  clusterLevel: {
    type: 'number',
    defaultValue: DEFAULT_AGGREGATION_LEVEL_CLUSTER - 1
  }
}

export default class ClusterTileLayer extends SpatialIndexLayer {
  constructor(props = {}) {
    super(props)
    this.registerVisConfig(DEFAULT_VIS_CONFIG)
  }

  public get type() {
    return LayerTypes.ClusterTile
  }

  get name() {
    return 'Cluster'
  }

  getDefaultLayerConfig(props = {}): any {
    const baseConfig = super.getDefaultLayerConfig(props)
    return {
      ...baseConfig,

      // Text
      textLabel: [
        {
          ...baseConfig.textLabel[0],
          color: [255, 255, 255],
          outlineColor: [44, 48, 50]
        }
      ]
    }
  }

  static findDefaultLayerProps(dataset: KeplerTable, foundLayers) {
    const emptyProps = { props: [] }

    if (foundLayers?.length) {
      return emptyProps
    }
    const scheme = getSchemeFromTilesetDataset(dataset)

    if (getDatasetType(dataset) === MapType.tileset && scheme === TilingScheme.Quadbin) {
      return {
        props: [
          {
            label: dataset.label,
            isVisible: true,
            columns: {}
          }
        ]
      }
    }
    return emptyProps
  }

  formatLayerData(datasets: any, oldLayerData: any) {
    const result = super.formatLayerData(datasets, oldLayerData)
    const { metadata } = datasets[this.config.dataId]

    return {
      ...result,
      formatter: getFormatter(TOOLTIP_FORMATS.DECIMAL_SHORT_COMMA.format),
      defaultAggregationExpColumnAlias: getDefaultAggregationExpColumnAliasForLayerType(
        this.type,
        metadata.provider,
        metadata.schema
      ),
      loadOptions: cartoSpatialTileLoadOptions
    }
  }

  renderLayer(opts: any) {
    if (!opts?.data?.data) return []

    const { data, gpuFilter } = opts
    const { formatTiles, geoColumn, scheme, sourceType } = this.meta
    const spatialIndex = geoColumn ? getSpatialIndexFromGeoColumn(geoColumn) : null
    const defaultLayerProps = this.getDefaultDeckLayerProps(opts)
    const pickable = this.hasInteractivity

    const updateTriggers = {
      ...this.getVisualChannelUpdateTriggers(),
      getFilterValue: gpuFilter.filterValueUpdateTriggers
    }

    return [
      new DeckGlClusterTileLayer({
        fetch: this.getCustomFetch(data.data as TilejsonResult),
        formatTiles,
        binary: true,
        updateTriggers,
        ...defaultLayerProps,
        ...data,
        data: data.data,
        pickable,
        highlightColor: this.config.highlightColor,
        autoHighlight: pickable,
        uniqueIdProperty: DEFAULT_UNIQUE_ID_PROPERTY,
        wrapLongitude: true,
        tileSize: getTileSize({
          tileResolution: data.data.tileResolution,
          scheme,
          sourceType,
          spatialIndex
        }),
        debounceTime: getDebounceTime(),
        onViewportLoad: (tiles: any) => this.onViewportLoad(tiles),
        lineWidthUnits: 'pixels',
        pointType: `circle+${this.config.visConfig.isTextVisible ? 'text' : ''}`,

        // Aggregation
        clusterLevel: this.config.visConfig.clusterLevel,

        // Color
        filled: this.config.visConfig.filled,

        // Stroke
        stroked: this.config.visConfig.stroked,
        lineWidthScale: this.config.visConfig.thickness,

        // Radius
        getWeight: (d) => d.properties[data.defaultAggregationExpColumnAlias],
        getPointRadius: (d, info: any) =>
          calculateRadius(
            d.properties,
            info.data.attributes.stats,
            this.config.visConfig.radiusRange as [number, number],
            data.defaultAggregationExpColumnAlias
          ),
        pointRadiusUnits: 'pixels',

        // Text
        textCharacterSet: 'auto',
        textFontFamily: 'Inter, sans',
        textFontSettings: { sdf: true },
        textFontWeight: 600,
        getText: (d) => `${data.formatter(d.properties[data.defaultAggregationExpColumnAlias])}`,
        getTextColor: this.config.textLabel[TEXT_LABEL_INDEX].color,
        textOutlineColor: [...(this.config.textLabel[TEXT_LABEL_INDEX].outlineColor as Color), TEXT_OUTLINE_OPACITY],
        textOutlineWidth: 5,
        textSizeUnits: 'pixels',
        getTextSize: (d, info: any) => {
          const radius = calculateRadius(
            d.properties,
            info.data.attributes.stats,
            this.config.visConfig.radiusRange as [number, number],
            data.defaultAggregationExpColumnAlias
          )

          return calculateTextFontSize(radius)
        }
      })
    ]
  }
}
